import React, { ReactNode } from 'react';
import '../styles/styles.css';
import './Label.css'
import './Text.css'
import { Footnote, Subhead } from './Text';
import { Space } from './Space';
import { VStack } from './Stack';

export interface LabelProps {
  /** The content to be rendered inside the Label. */
  children?: ReactNode;

  /** The primary text content of the Label. */
  label: any;

  /** Determines the alignment of the Label's content. */
  horizontal?: boolean;

  /**
   * Ratio for the alignment spacing.
   * Example: `ratio = [1, 2]` sets a 1:2 spacing ratio for the text and children.
   */
  ratio?: [number, number];

  labelClass?: 'Title1' | 'Title2' | 'Title3' | 'Headline' | 'Body' | 'Footnote' | 'Caption1' | 'Caption2';

  labelColor?: string;

  width?: 'hug' | 'fill';

  helperText?: string;

  error?: boolean;

  onErrorResolve?: () => void;

}

/**
 * # Label
 * A flexible and reusable label component with ratio-based spacing.
 */
const Label: React.FC<LabelProps> = ({
  children,
  label,
  horizontal = false,
  ratio,
  labelClass = 'Subhead',
  labelColor = 'text-secondary',
  width = 'fill',
  helperText,
  error,
  onErrorResolve
}) => {

  const handleChildClick = (event: React.MouseEvent) => {
    if (error && onErrorResolve) {
      onErrorResolve();
    }
  };

  const style = horizontal && ratio
    ? {
      display: 'grid',
      gridTemplateColumns: `${ratio[0]}fr ${ratio[1]}fr`,
      gap: 'var(--ts-space-content)',
    }
    : undefined;



  return (
    <div className={`label-wrapper width-${width}`}>
      <label
        className={`nested ${!horizontal ? 'v-stack' : 'h-stack middle'} width-${width} ${error ? 'error' : ''}`}
        style={style} >
        {horizontal ?
          <VStack>
            <p className={`text ${labelClass.toLowerCase()} color-${labelColor} width-${width}`} >
              {label}
            </p>
            {helperText && horizontal && <Footnote color={error ? 'negative' : 'text-secondary'}>{helperText}</Footnote>}
          </VStack>
          :
          <p className={`text ${labelClass.toLowerCase()} color-${labelColor} width-${width}`} >
            {label}
          </p>

        }
        {children && !horizontal && <Space.Content />}
        {children && horizontal && !ratio && <Space.Content />}
        {children && (
          <div
            onClick={handleChildClick}
            className={`label-content v-stack width-${width} ${horizontal ? 'right' : 'left'}`}>
            {children}
            {helperText && !horizontal && <Footnote color={error ? 'negative' : 'text-secondary'}>{helperText}</Footnote>}
          </div>
        )}
      </label>
    </div>
  );
};

export { Label };
